<template>
  <div class="bg-surface-50 dark:bg-surface-950 dark:circuit-bg min-h-screen flex items-center px-4 py-10 md:px-12 lg:px-20 login-container">
    <div class="tech-panel p-4 sm:p-6 shadow w-full sm:w-10/12 md:w-8/12 lg:w-6/12 mx-auto relative login-card">
      <!-- Add tech corner accents -->
      <div class="tech-corner top-left" />
      <div class="tech-corner top-right" />
      <div class="tech-corner bottom-left" />
      <div class="tech-corner bottom-right" />

      <div class="text-center mb-6 md:mb-8 relative">
        <img
          src="~/assets/images/logo.svg"
          alt="Krewebacchus Logo"
          class="logo mb-2 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto logo-glow"
        />

        <div
          class="text-xl sm:text-2xl md:text-3xl font-medium mb-2 md:mb-4 krewebacchus-title"
          :class="isStaging ? 'text-red-600 dark:text-red-400' : 'text-surface-800 dark:text-primary-500'"
        >
          krewebacchus{{ isStaging ? ' staging' : '' }}
        </div>

        <!-- Staging warning banner -->
        <div
          v-if="isStaging"
          class="inline-flex items-center px-3 py-1 text-sm font-medium text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-full border border-red-200 dark:border-red-800 mb-2"
        >
          <i class="pi pi-exclamation-triangle mr-2" />
          Staging Environment
        </div>

        <!-- Add a sci-fi tagline -->
        <div class="text-sm text-surface-600 dark:text-surface-400 terminal-text">
          <span class="typing-text">System v3.0 // Establish Connection</span>
        </div>

        <!-- Add dark mode toggle and logout button -->
        <div class="absolute top-0 right-0 flex gap-2">
          <button
            v-if="isAuthenticated"
            v-tooltip.bottom="'Logout'"
            class="p-2 tech-button rounded-full text-surface-600 dark:text-surface-300 hover:text-red-500 dark:hover:text-red-400"
            aria-label="Logout"
            @click="handleLogout"
          >
            <i class="pi pi-sign-out text-xl" />
          </button>
          <button
            v-tooltip.bottom="isDarkMode ? 'Light Mode' : 'Dark Mode'"
            class="p-2 tech-button rounded-full text-surface-600 dark:text-surface-300 hover:text-primary-500 dark:hover:text-primary-400"
            aria-label="Toggle dark mode"
            @click="toggleDarkMode"
          >
            <i
              :class="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"
              class="text-xl"
            />
          </button>
        </div>
      </div>

      <div>
        <ClientOnly>
          <slot />
        </ClientOnly>
      </div>
      <div class="text-justify-center text-sm text-surface-600 dark:text-surface-400 mt-4">
        <p class="text-center">
          <span>© 2025 Intergalactic Krewe of Chewbacchus. All rights reserved.</span>
        </p>
        <p class="text-center">
          <span>
            <a href="/privacy-policy">Privacy policy</a> | <a href="/terms-of-service">Terms of service</a>
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useCurrentUser } from 'vuefire'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useAuth } from '~/composables/useAuth'

const isDarkMode = ref(false)
const runtimeConfig = useRuntimeConfig()
const currentUser = useCurrentUser()
const router = useRouter()
const toast = useToast()
const { signOutUser } = useAuth()

// Check if user is authenticated
const isAuthenticated = computed(() => !!currentUser.value)

// Environment detection
const isStaging = computed(() => {
  return runtimeConfig.public.krewebacchusEnv === 'staging'
})

onMounted(() => {
  // Check if dark mode is active
  isDarkMode.value = document.documentElement.classList.contains('dark')
})

const toggleDarkMode = () => {
  // Toggle dark mode
  document.documentElement.classList.toggle('dark')
  isDarkMode.value = !isDarkMode.value

  // Store the preference
  localStorage.setItem('theme', isDarkMode.value ? 'dark' : 'light')
}

const handleLogout = async () => {
  try {
    await signOutUser()
    toast.add({
      severity: 'success',
      summary: 'Logged Out',
      detail: 'You have been successfully logged out',
      life: 3000,
    })
    await router.push('/login')
  }
  catch (error) {
    console.error('Logout error:', error)
    toast.add({
      severity: 'error',
      summary: 'Logout Failed',
      detail: 'An error occurred while logging out',
      life: 3000,
    })
  }
}
</script>

<style scoped>
.krewebacchus-title {
  font-family: "Starjedi", sans-serif;
  letter-spacing: 0.05em;
  text-transform: lowercase;
  line-height: 1.2;
  text-shadow: 0 0 5px rgba(57, 255, 20, 0.4);
}

.dark .krewebacchus-title {
  text-shadow: 0 0 5px currentColor;
}

/* Add more responsive font styling for mobile */
@media (max-width: 640px) {
  .krewebacchus-title {
    font-size: 1.5rem;
    letter-spacing: 0.03em;
  }
}

@media (max-width: 375px) {
  .krewebacchus-title {
    font-size: 1.25rem;
    letter-spacing: 0.01em;
  }
}

.login-container {
  position: relative;
  overflow: hidden;
  background-image: radial-gradient(circle at 50% 50%, rgba(57, 255, 20, 0.03) 0%, transparent 70%);
}

.login-container::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 49%, rgba(57, 255, 20, 0.1) 50%, transparent 51%);
  background-size: 20px 20px;
  z-index: 1;
  animation: bg-animation 5s linear infinite;
  pointer-events: none;
}

.dark .login-container::before {
  background: linear-gradient(45deg, transparent 49%, rgba(57, 255, 20, 0.05) 50%, transparent 51%);
}

.login-card {
  backdrop-filter: blur(5px);
  border: 1px solid rgba(57, 255, 20, 0.5);
  background-color: rgba(255, 255, 255, 0.85);
  z-index: 2;
}

.dark .login-card {
  border: 1px solid rgba(57, 255, 20, 0.3);
  background-color: rgba(10, 16, 24, 0.6);
}

.logo-glow {
  filter: drop-shadow(0 0 5px rgba(57, 255, 20, 0.3));
}

.dark .logo-glow {
  filter: drop-shadow(0 0 8px rgba(57, 255, 20, 0.7));
}

/* Tech corner accents */
.tech-corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(57, 255, 20, 0.5);
  z-index: 3;
}

.dark .tech-corner {
  border: 2px solid rgba(57, 255, 20, 0.7);
}

.tech-corner.top-left {
  top: -1px;
  left: -1px;
  border-right: none;
  border-bottom: none;
}

.tech-corner.top-right {
  top: -1px;
  right: -1px;
  border-left: none;
  border-bottom: none;
}

.tech-corner.bottom-left {
  bottom: -1px;
  left: -1px;
  border-right: none;
  border-top: none;
}

.tech-corner.bottom-right {
  bottom: -1px;
  right: -1px;
  border-left: none;
  border-top: none;
}

/* Typing animation */
.typing-text {
  display: inline-block;
  overflow: hidden;
  border-right: 3px solid rgba(57, 255, 20, 0.5);
  white-space: nowrap;
  animation: typing 3s steps(40) 1s 1 normal both, blink-caret 0.75s step-end infinite;
}

.dark .typing-text {
  border-right: 3px solid rgba(57, 255, 20, 0.7);
}

@keyframes typing {
  from { width: 0 }
  to { width: 100% }
}

@keyframes blink-caret {
  from, to { border-color: transparent }
  50% { border-color: rgba(57, 255, 20, 0.7) }
}

@keyframes bg-animation {
  0% { transform: translate(0, 0); }
  100% { transform: translate(20px, 20px); }
}

@media (max-width: 375px) {
  .krewebacchus-title {
    font-size: 1.25rem;
    letter-spacing: 0.01em;
  }

  .logo {
    width: 3rem;
    height: 3rem;
  }
}
</style>
